import React, { useState, useRef, useEffect } from 'react';
import { useQCM } from '../../contexts/QCMContext';
import { useTranslation } from 'react-i18next';

const QCMTextInput = () => {
    const { t } = useTranslation();
    const {
        currentQCM,
        freeTextAnswer,
        setFreeTextAnswer,
        submitFreeTextAnswer,
        loading,
        setIsTimerRunning
    } = useQCM();

    const [inputValue, setInputValue] = useState(freeTextAnswer || '');
    const textareaRef = useRef(null);

    useEffect(() => {
        setInputValue(freeTextAnswer || '');
    }, [freeTextAnswer]);

    const handleInputChange = (e) => {
        const value = e.target.value;
        setInputValue(value);
        setFreeTextAnswer(value);
        
        // Start timer when user starts typing
        if (value.trim() && !loading) {
            setIsTimerRunning(true);
        }
    };

    const handleSubmit = async () => {
        if (!inputValue.trim() || loading) return;
        
        try {
            await submitFreeTextAnswer(inputValue.trim());
        } catch (error) {
            console.error('Error submitting answer:', error);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
    };

    if (!currentQCM) {
        return (
            <div className="text-center py-8">
                <p className="text-gray-400">{t('no_qcm_selected')}</p>
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col">
            {/* Header */}
            <div className="flex-shrink-0 mb-4">
                <h3 className="text-lg font-semibold text-blue-400 mb-2 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                    {t('your_answer')}
                </h3>
                <p className="text-sm text-gray-400">
                    {t('answer_before_options')}
                </p>
            </div>

            {/* Text Input Area */}
            <div className="flex-grow flex flex-col">
                <div className="flex-grow mb-4">
                    <textarea
                        ref={textareaRef}
                        value={inputValue}
                        onChange={handleInputChange}
                        onKeyPress={handleKeyPress}
                        placeholder={t('type_your_answer_here')}
                        className="w-full h-full bg-gray-700 text-white border border-gray-600 rounded-md p-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-none"
                        disabled={loading}
                    />
                </div>

                {/* Controls */}
                <div className="flex-shrink-0">
                    <div className="flex items-center justify-between mb-4">
                        <div className="text-sm text-gray-400">
                            {inputValue.length} {t('characters')}
                        </div>
                        
                        <button
                            onClick={handleSubmit}
                            disabled={!inputValue.trim() || loading}
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors flex items-center"
                        >
                            {loading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    {t('evaluating')}
                                </>
                            ) : (
                                <>
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                    </svg>
                                    {t('submit_answer')}
                                </>
                            )}
                        </button>
                    </div>

                    {/* Instructions */}
                    <div className="text-xs text-gray-400 bg-gray-700 rounded p-2">
                        <p className="mb-1">
                            <kbd className="px-1 py-0.5 bg-gray-600 rounded text-xs">Enter</kbd> {t('to_submit')}
                        </p>
                        <p>
                            <kbd className="px-1 py-0.5 bg-gray-600 rounded text-xs">Shift + Enter</kbd> {t('for_new_line')}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QCMTextInput;